import api from '../utils/api';

// Types
export interface Modality {
  modalityId: number;
  modalityName: string;
  modality: string;
  description: string;
  isActive?: boolean;
  defaultDuration?: number;
  scheduleModalityId?: string;  // GUID from ScheduleModality.Id (required for AddToSchedule)
}

export interface ScheduleStatus {
  accessionNumber: string;
  status: string;
  statusId: number;
  studyId?: string;
  imagesAvailable: boolean;
  reportAvailable: boolean;
}

export interface KeystoneThumbnail {
  imageId: string;
  position: number | null;
  attachmentId: string;
  fileName: string;
  fileSize?: number;
  downloadUrl: string;
  publicUrl: string;
}

export interface KeystoneReportPdf {
  attachmentId: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  downloadUrl: string;
  publicUrl: string;
}

export interface Study {
  studyId: string;
  patientId: string;
  patientName: string;
  accessionNumber: string;
  studyDate: string;
  modalityId: number;
  modalityName?: string;
  studyDescription?: string;
  studyStatusId: number;
  numberOfImages?: number;
  responsiblePerson?: string;
  reportAvailable?: boolean;
  viewerUrl?: string | null;
  thumbnails?: KeystoneThumbnail[];
}

export interface Report {
  reportId: string;
  studyId: string;
  reportText: string;
  reportDate: string;
  reportingRadiologist: string;
  reportStatus: string;
  isFinalized: boolean;
  impressions?: string;
  findings?: string;
  technique?: string;
  comparison?: string;
  pdf?: KeystoneReportPdf | null;
}

export interface SearchStudiesRequest {
  startDate: string;
  endDate: string;
  patientName?: string;
  accessionNumber?: string;
  modalityId?: number;
  studyStatusId?: number;
}

export interface KeystoneStatus {
  connected: boolean;
  syncState: {
    lastSyncTime?: string;
    syncInProgress: boolean;
    lastError?: string;
  };
}

export type ImagingSubmissionStatus = 'DRAFT' | 'SUBMITTED' | 'CANCELLED';

export interface ImagingRequest {
  id: string;
  horseId: string;
  clinicId: string;
  soapNoteId?: string | null;
  accessionNumber: string;
  modalityId?: number | null;
  modalityName?: string | null;
  bodyRegion?: string | null;
  clinicalHistory?: string | null;
  requestedDateTime: string;
  requestedBy?: string | null;
  priority?: string | null;
  specialNeeds?: string | null;
  preMedication?: string | null;
  requestedProcedureDescription?: string | null;
  admittingDiagnosisDescription?: string | null;
  notes?: string | null;
  location?: string | null;
  submissionStatus: ImagingSubmissionStatus;
  submittedAt?: string | null;
  submittedBy?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateImagingRequestPayload {
  horseId: string;
  soapNoteId?: string;
  modalityId?: number;
  modalityName?: string;
  bodyRegion?: string;
  clinicalHistory?: string;
  priority?: string;
  specialNeeds?: string;
  preMedication?: string;
  requestedProcedureDescription?: string;
  admittingDiagnosisDescription?: string;
  notes?: string;
  location?: string;
  // ISO-8601 string. May be naive (no Z) — backend stores as Date.
  requestedDateTime?: string;
  submitImmediately?: boolean;
}

export interface HorseStudySummary {
  studyId: string;
  accessionNumber: string;
  patientName?: string | null;
  studyDate: string | null;
  modalityId?: number | null;
  modalityName?: string | null;
  studyDescription?: string | null;
  studyStatusId?: number | null;
  numberOfImages?: number | null;
  reportAvailable: boolean;
  viewerUrl: string | null;
  thumbnails: KeystoneThumbnail[];
  reportPdf: KeystoneReportPdf | null;
}

// Service class
class KeystoneService {
  /**
   * Whether the caller's own clinic has Keystone connected
   */
  async getStatus(): Promise<KeystoneStatus> {
    const response = await api.get('/keystone/status');
    return response.data;
  }

  /**
   * Get available imaging modalities
   */
  async getModalities(): Promise<Modality[]> {
    const response = await api.get('/keystone/modalities');
    return response.data;
  }

  /**
   * Get schedule status by accession number
   */
  async getScheduleStatus(accessionNumber: string): Promise<ScheduleStatus> {
    const response = await api.get(`/keystone/schedule-status/${accessionNumber}`);
    return response.data;
  }

  /**
   * Search for studies
   */
  async searchStudies(request: SearchStudiesRequest): Promise<Study[]> {
    const response = await api.post('/keystone/studies/search', request);
    return response.data;
  }

  /**
   * Get study details
   */
  async getStudy(studyId: string): Promise<Study> {
    const response = await api.get(`/keystone/studies/${studyId}`);
    return response.data;
  }

  /**
   * Get reports for a study
   */
  async getStudyReports(studyId: string): Promise<Report[]> {
    const response = await api.get(`/keystone/studies/${studyId}/reports`);
    return response.data;
  }

  /**
   * Get DICOM viewer URL for a study
   */
  async getViewerUrl(studyId: string, accessionNumber: string): Promise<{
    viewerUrl: string;
    studyId: string;
  }> {
    const response = await api.get(
      `/keystone/studies/${studyId}/viewer-url?accessionNumber=${accessionNumber}`
    );
    return response.data;
  }

  /**
   * Get recent studies for the clinic
   */
  async getRecentStudies(): Promise<Study[]> {
    const response = await api.get('/keystone/recent-studies');
    return response.data;
  }

  /**
   * Trigger manual sync
   */
  async triggerSync(): Promise<{
    success: boolean;
    data?: any;
    error?: string;
    message: string;
  }> {
    const response = await api.post('/keystone/sync');
    return response.data;
  }

  // ───── Imaging requests (DRAFT → SUBMITTED) ──────────────────────────

  async createImagingRequest(payload: CreateImagingRequestPayload): Promise<ImagingRequest> {
    const response = await api.post('/keystone/imaging-requests', payload);
    return response.data;
  }

  /**
   * Edit a DRAFT imaging request. Backend refuses if status is SUBMITTED
   * or CANCELLED — the UI should hide / disable the edit affordance for
   * those rows. Pass only the fields you want to change; omitted keys are
   * left untouched server-side.
   */
  async updateImagingRequest(
    id: string,
    patch: Partial<Omit<CreateImagingRequestPayload, 'horseId' | 'submitImmediately' | 'soapNoteId'>>,
  ): Promise<ImagingRequest> {
    const response = await api.patch(`/keystone/imaging-requests/${id}`, patch);
    return response.data;
  }

  async listImagingRequests(
    horseId: string,
    status?: ImagingSubmissionStatus,
  ): Promise<ImagingRequest[]> {
    const response = await api.get('/keystone/imaging-requests', {
      params: { horseId, ...(status ? { status } : {}) },
    });
    return response.data;
  }

  async submitImagingRequest(
    id: string,
    body?: { modalityId?: number; scheduleModalityId?: string; scheduleDateTime?: string; useQueue?: boolean },
  ): Promise<ImagingRequest> {
    const response = await api.post(`/keystone/imaging-requests/${id}/submit`, body ?? {});
    return response.data;
  }

  async cancelImagingRequest(id: string, reason?: string): Promise<ImagingRequest> {
    const response = await api.post(`/keystone/imaging-requests/${id}/cancel`, { reason });
    return response.data;
  }

  async deleteImagingRequest(id: string): Promise<void> {
    await api.delete(`/keystone/imaging-requests/${id}`);
  }

  // ───── Horse studies (for Discharge Summary picker) ──────────────────

  async getStudiesForHorse(
    horseId: string,
    opts?: { limit?: number; thumbnails?: number; from?: string; to?: string },
  ): Promise<HorseStudySummary[]> {
    const response = await api.get(`/keystone/horse/${horseId}/studies`, {
      params: opts ?? {},
    });
    return response.data;
  }

  // ───── Settings → Integrations: credential form ────────────────────────

  async getConfig(): Promise<KeystoneConfig> {
    const response = await api.get('/keystone/config');
    return response.data;
  }

  async testConfig(payload: KeystoneConfigPayload): Promise<KeystoneConfigTestResult> {
    const response = await api.post('/keystone/config/test', payload);
    return response.data;
  }

  async saveConfig(payload: KeystoneConfigPayload): Promise<KeystoneConfig> {
    const response = await api.post('/keystone/config', payload);
    return response.data;
  }

  async deleteConfig(): Promise<{ ok: true }> {
    const response = await api.delete('/keystone/config');
    return response.data;
  }
}

export interface KeystoneConfigPayload {
  keystoneClinicCode: string;
  apiUsername: string;
  apiPassword: string;
  workstationName?: string;
}

export type KeystoneConfig =
  | { configured: false }
  | {
      configured: true;
      enabled: boolean;
      keystoneClinicCode: string;
      apiUsername: string;
      workstationName: string;
      pollIntervalSec: number;
      lastConnectedAt: string | null;
      lastPolledAt: string | null;
      lastError: string | null;
      lastErrorAt: string | null;
    };

export interface KeystoneConfigTestResult {
  success: boolean;
  message: string;
  modalityCount?: number;
  error?: string;
}

// Export singleton instance
export const keystoneService = new KeystoneService();
